import * as React from 'react';
import { Grid, GridItem, PageSection, Title } from '@patternfly/react-core';
import PageModel from '@app/Model/PageModel';
import PageLoader from '@app/Page/PageLoader';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';
import DashBoardPermissionInterface from '@app/Interface/DashboardPermissionInterface';
import RecentResults from '@app/Components/Dashboard/RecentResult';
import MonthToDate from '@app/Components/Dashboard/MonthToDate';
import DailyChurn from '@app/Components/Dashboard/DailyChurn';
import UpcomingEvents from '@app/Components/Dashboard/UpcomingEvents';
import LatestArticles from '@app/Components/Dashboard/LatestArticles';
import MilkPrices from '@app/Components/Dashboard/MilkPrices';
import { UserContext } from '@app/Context/UserContext';
import UserAuthentication from '@app/Security/UserAuthentication';
import htmlParser from '@app/utils/htmlParser';
import DashboardNewsModel from '@app/Model/DashboardNewsModel';
import DashboardHelper from '@app/Service/DashboardHelper';
import PageRouteHelper from '@app/Service/PageRouteHelper';
import { PageRouteContext } from '@app/Context/PageRouteContext';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import DashboardDataModel from '@app/Model/DashboardDataModel';

interface DashboardProps {
    pageData: PageModel,
}

const Dashboard: React.FC<DashboardProps> = ({ pageData }) => {

    const reportTemplateName = "report_template.php";

    const initialPermissions: DashBoardPermissionInterface = {
        viewRecentResults: false,
        viewCurrentComponent: false,
        viewHistoricComponent: false,
        viewQualityComponents: false,
        viewHistoricQuality: false,
        viewMonthToDate: false,
        viewNews: false,
        viewEvents: false,
        viewDailyChurn: true,
        viewMilkPrices: false,
        viewCoop: false,
        viewMontana: false,
    };

    const [isNewsLoading, setIsNewsLoading] = React.useState(true);

    const [isDataLoading, setIsDataLoading] = React.useState(true);

    const [dashPermissions, setDashPermissions] = React.useState(initialPermissions);

    const { userState } = React.useContext(UserContext);

    const [dashboardNews, setDashboardNews] = React.useState(new DashboardNewsModel());

    const { pageRouteState } = React.useContext(PageRouteContext);

    const articlePageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_ARTICLE_PAGE);

    const articlesLimit = Number(process.env.DASHBOARD_ARTICLES_TO_DISPLAY ?? 5);

    const eventPageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_EVENT_PAGE);

    const eventsLimit = Number(process.env.DASHBOARD_EVENTS_TO_DISPLAY ?? 5);

    const [dashboardData, setDashboardData] = React.useState(new DashboardDataModel());

    const currentComponentsPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_DAILY_COMPONENTS);

    const qualityComponentsPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_CURRENT_QUALITY);

    const componentsLimit = Number(process.env.DASHBOARD_RECENT_RESULTS_TO_DISPLAY) ?? 5;

    const coopPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_COOP_PRICE_PROJECTION);

    const montanaPageRoute = PageRouteHelper.getPageRouteByReportType(pageRouteState.pageRoutes, TemplateDefinitions.REPORT_TYPE_MONTANA_PRICE_PROJECTION);

    const [updateNewsCacheInBackground, setUpdateNewsCacheInBackground] = React.useState(false);

    // determine if user can view recent results 
    // based on page permissions
    React.useEffect(() => {

        // make sure we have a positive fpu id
        if (userState.user.currentFpuId) {

            // copy current permissions
            const permissions = { ...dashPermissions };

            permissions.viewCurrentComponent = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, reportTemplateName, "daily-components");

            permissions.viewQualityComponents = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, reportTemplateName, "current-quality");

            permissions.viewHistoricQuality = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, reportTemplateName, "historical-quality");

            if (permissions.viewCurrentComponent || permissions.viewQualityComponents) {
                permissions.viewRecentResults = true;
            }

            permissions.viewMonthToDate = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, reportTemplateName, "daily-components");

            permissions.viewEvents = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, "events_template.php");

            permissions.viewNews = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, "articles_template.php");

            permissions.viewCoop = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, reportTemplateName, "milk-pricing-coop");

            permissions.viewMontana = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, reportTemplateName, "milk-pricing-montana");

            if (permissions.viewCoop || permissions.viewMontana) {
                permissions.viewMilkPrices = true;
            } else {
                permissions.viewMilkPrices = false;
            }

            setDashPermissions(permissions);

        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState.user.currentFpuId]);

    // get dashboard data
    // call this first, since it is faster than getting news
    React.useEffect(()=> {

        const controller = new AbortController();

        let mounted = true;

        const getDashboardData = async () => {

            const data = await DashboardHelper.getDashboardData(controller, currentComponentsPageRoute.id, qualityComponentsPageRoute.id, componentsLimit, coopPageRoute.id, montanaPageRoute.id, userState.user.currentFpuId);

            setIsDataLoading(false);

            if (mounted) {
                setDashboardData(data);
            }

        }

        if (userState.user.currentFpuId && currentComponentsPageRoute.id && qualityComponentsPageRoute.id && coopPageRoute.id && montanaPageRoute.id) {

            setIsDataLoading(true);

            getDashboardData();

        }

        return () => {
            mounted = false;
            controller?.abort;
        };

    }, [componentsLimit, coopPageRoute.id, currentComponentsPageRoute.id, montanaPageRoute.id, qualityComponentsPageRoute.id, userState.user.currentFpuId]);

    // get the dashboard news
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        const getDashboardNews = async () => {

            const news = await DashboardHelper.getDashboardNews(controller, userState.user.currentFpuId, articlePageRoute.id, articlesLimit, eventPageRoute.id, eventsLimit);

            setIsNewsLoading(false);

            if (mounted) {
                setDashboardNews(news);
                setUpdateNewsCacheInBackground(true);
            }

        }

        if (userState.user.currentFpuId && articlePageRoute.id && eventPageRoute.id) {

            setIsNewsLoading(true);

            getDashboardNews();

        }

        return () => {
            mounted = false;
            controller?.abort;
        };


    }, [articlePageRoute.id, articlesLimit, eventPageRoute.id, eventsLimit, userState.user.currentFpuId]);

    // TODO: Try adding caching news later
    // update news data in the background
    // React.useEffect(() => {

    //     if (updateNewsCacheInBackground) {

    //         DashboardHelper.getDashboardNewsNonAsync(userState.user.currentFpuId, articlePageRoute.id, articlesLimit, eventPageRoute.id, eventsLimit);

    //         setUpdateNewsCacheInBackground(false);
    //     }

    // }, [articlePageRoute.id, articlesLimit, eventPageRoute.id, eventsLimit, updateNewsCacheInBackground, userState.user.currentFpuId]);

    return (
        <>
            {pageData.id === 0
                ? <PageLoader />
                : <ToastNotifications>
                    <PageSection className="pf-u-pb-0">
                        <Title headingLevel="h1" className="pf-u-mb-md">{pageData.title}</Title>
                        <div className="pf-c-content">
                            {htmlParser.htmlToJSX(pageData.content ?? '')}
                        </div>
                    </PageSection>
                    {dashPermissions.viewRecentResults && <RecentResults isLoading={isDataLoading} dashPermissions={dashPermissions} currentComponents={dashboardData.currentComponents} />}
                    <PageSection>
                        <Grid
                            hasGutter
                            sm={12}
                            md={6}
                            lg={6}
                            xl={6}
                            xl2={6}
                        >
                            {dashPermissions.viewMonthToDate && <GridItem><MonthToDate isLoading={isDataLoading} monthToDate={dashboardData.monthToDate} /></GridItem>}
                            {dashPermissions.viewDailyChurn && <GridItem><DailyChurn pageData={pageData} currentFpuId={userState.user.currentFpuId} /></GridItem>}
                            {dashPermissions.viewEvents && <GridItem><UpcomingEvents isLoading={isNewsLoading} eventPageRoute={eventPageRoute} events={dashboardNews.events} /></GridItem>}
                            {dashPermissions.viewNews && <GridItem><LatestArticles isLoading={isNewsLoading} articlePageRoute={articlePageRoute} articles={dashboardNews.articles} /></GridItem>}
                        </Grid>
                    </PageSection>
                    {dashPermissions.viewMilkPrices && <MilkPrices dashPermissions={dashPermissions} coopPageRoute={coopPageRoute} montanaPageRoute={montanaPageRoute} isLoading={isDataLoading} coopProjections={dashboardData.pricingCoop} montanaProjections={dashboardData.pricingMontana} />}
                </ToastNotifications>}
        </>
    );

}

export { Dashboard };
